import React, { useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import {
  ArrowForward as ArrowForwardIcon,
  CheckCircleOutline as CheckCircleOutlineIcon,
  Close as CloseIcon,
  GridViewOutlined as GridViewOutlinedIcon,
  InfoOutlined as InfoOutlinedIcon,
  Inventory2Outlined as Inventory2OutlinedIcon,
  LayersOutlined as LayersOutlinedIcon,
  SettingsOutlined as SettingsOutlinedIcon,
  WarningAmberRounded as WarningAmberRoundedIcon,
} from "@mui/icons-material";
import MobileVesselPositionGrid from "../mobile/terminalCall/MobileVesselPositionGrid";
import VesselPlannerSettingsDialog from "./VesselPlannerSettingsDialog";
import {
  calculateCohortSpaceRequirement,
  getContainerGridSpan,
  getContainerTeu,
} from "./autoPlannerRules";
import {
  getExcludedPlannerPositionKeys,
  normalizeVesselPlannerSetting,
  plannerPositionKey,
} from "./plannerSettings";

type AutoPlannerDialogProps = {
  open: boolean;
  vessel: any;
  terminalCall: any;
  planningContainers: any[];
  allContainers: any[];
  nextTerminalCall?: any;
  processing?: boolean;
  onClose: () => void;
  onConfirm: (maximumLayers: number) => void;
  onVesselUpdated?: (vessel: any) => void;
};

type AutoPlannerVesselGridProps = {
  vessel: any;
  containers: any[];
  dockingSide: string;
};

const AutoPlannerVesselGrid = ({
  vessel,
  containers,
  dockingSide,
}: AutoPlannerVesselGridProps) => {
  const viewportRef = useRef<HTMLDivElement | null>(null);
  const [scale, setScale] = useState(1);
  const [height, setHeight] = useState<number | undefined>();

  useEffect(() => {
    const viewport = viewportRef.current;
    const planner = viewport?.querySelector<HTMLElement>(
      ".mobile-position-planner"
    );
    const grid = viewport?.querySelector<HTMLElement>(
      ".mobile-position-grid-scroll"
    );
    const ship = viewport?.querySelector<HTMLElement>(".mobile-position-ship");
    if (!viewport || !planner || !grid || !ship) return;

    const fitGrid = () => {
      const availableWidth = viewport.clientWidth;
      const naturalWidth = ship.scrollWidth;
      const nextScale =
        naturalWidth > 0 ? Math.min(1, availableWidth / naturalWidth) : 1;
      const nonGridHeight = planner.scrollHeight - grid.offsetHeight;

      setScale(nextScale);
      setHeight(Math.ceil(nonGridHeight + grid.offsetHeight * nextScale));
    };

    fitGrid();
    const resizeObserver = new ResizeObserver(fitGrid);
    resizeObserver.observe(viewport);
    resizeObserver.observe(ship);

    return () => resizeObserver.disconnect();
  }, [containers, vessel]);

  return (
    <Box
      ref={viewportRef}
      sx={{
        position: "relative",
        width: "100%",
        overflow: "hidden",
        "& .mobile-position-planner": {
          height,
          overflow: "hidden",
        },
        "& .mobile-position-grid-scroll": {
          overflowX: "visible",
          transform: `scale(${scale})`,
          transformOrigin: "top left",
        },
      }}
    >
      <MobileVesselPositionGrid
        vessel={vessel}
        containers={containers}
        direction="loading"
        dockingSide={dockingSide}
      />
    </Box>
  );
};

const normalizeId = (value: any) => {
  if (!value) return null;
  if (typeof value === "string") return value.replace(/^\/api\/[^/]+\//, "");
  return normalizeId(value["@id"]);
};

const destinationName = (container: any, unknownLabel: string) =>
  container?.unloadingTerminalCall?.terminal?.name ||
  container?.unloadingTerminal?.name ||
  container?.unloadingTerminalCall?.terminal?.abbreviation ||
  container?.unloadingTerminal?.abbreviation ||
  unknownLabel;

export const getRequiredGroundTeu = (
  containers: any[],
  maximumLayers: number
) => {
  let twentyFt = 0;
  let fortyFt = 0;
  containers.forEach((container) => {
    if (getContainerGridSpan(container) >= 2) fortyFt += 1;
    else twentyFt += 1;
  });
  const requirement = calculateCohortSpaceRequirement(
    twentyFt,
    fortyFt,
    maximumLayers
  );
  // A destination's 20ft bases can reuse its own 40ft pair footprints (a 2-TEU
  // bridge over two same-destination 1-TEUs), but each bridged pair holds one
  // fewer 40-foot container, so only the 40ft capacity headroom may be bridged
  // or the 40-foot cargo is stranded.
  const headroom = Math.max(
    0,
    requirement.twoTeuFootprints * maximumLayers - fortyFt
  );
  const bridgePairs = Math.min(
    Math.floor(requirement.oneTeuFootprints / 2),
    headroom
  );
  const bridgeOverlap = bridgePairs * 2;
  return requirement.totalFloorCells - bridgeOverlap;
};

const AutoPlannerDialog = ({
  open,
  vessel,
  terminalCall,
  planningContainers,
  allContainers,
  nextTerminalCall,
  processing = false,
  onClose,
  onConfirm,
  onVesselUpdated,
}: AutoPlannerDialogProps) => {
  const { t } = useTranslation();
  const layerButtonCount = Math.max(1, vessel?.positions?.length ?? 1);
  // Layer 5 is temporarily excluded from the autoplanner until it can stack
  // that tier reliably; every other configured vessel layer stays selectable.
  const temporarilyExcludedLayers = new Set<number>([5]);
  const selectableLayers = Array.from(
    { length: layerButtonCount },
    (_, index) => index + 1
  ).filter((layers) => !temporarilyExcludedLayers.has(layers));
  const maximumVesselLayers = Math.max(1, ...selectableLayers);
  const [maximumLayers, setMaximumLayers] = useState(
    Math.min(3, maximumVesselLayers)
  );
  const [settingsOpen, setSettingsOpen] = useState(false);

  useEffect(() => {
    if (open) setMaximumLayers(Math.min(3, maximumVesselLayers));
  }, [open, maximumVesselLayers]);

  const eligibleContainers = useMemo(() => {
    const terminalCallId = normalizeId(terminalCall?.["@id"]);

    return (planningContainers || []).filter(
      (container: any) =>
        (!Array.isArray(container?.position) || container.position.length === 0) &&
        normalizeId(container?.loadingTerminalCall?.["@id"]) === terminalCallId
    );
  }, [planningContainers, terminalCall]);

  const floorCapacity = useMemo(() => {
    const floor = vessel?.positions?.[0];
    if (!Array.isArray(floor)) return 0;
    return floor.reduce(
      (total: number, slots: any) => total + Math.max(0, Number(slots) || 0),
      0
    );
  }, [vessel]);

  const occupiedFloorTeu = useMemo(() => {
    const vesselId = normalizeId(vessel?.["@id"]);
    const currentCallId = normalizeId(terminalCall?.["@id"]);
    const nextCallId = normalizeId(nextTerminalCall?.["@id"]);
    const relevantPlanningIds = new Set(
      (planningContainers || []).map((container: any) => normalizeId(container?.["@id"]))
    );
    const occupied = new Set<string>();

    (allContainers || []).forEach((container: any) => {
      if (!Array.isArray(container?.position) || container.position.length < 3) return;
      const containerVesselId = normalizeId(container?.vessel?.["@id"]);
      if (vesselId && containerVesselId && containerVesselId !== vesselId) return;

      const containerId = normalizeId(container?.["@id"]);
      const loadingCallId = normalizeId(container?.loadingTerminalCall?.["@id"]);
      const unloadingCallId = normalizeId(container?.unloadingTerminalCall?.["@id"]);
      const isRelevant =
        relevantPlanningIds.has(containerId) ||
        Boolean(nextCallId && loadingCallId === nextCallId);

      // Unloading containers at this call are not part of the departure stowage.
      if (!isRelevant || unloadingCallId === currentCallId) return;

      const [layer, x, y] = container.position;
      if (Number(layer) !== 1) return;

      for (
        let offset = 0;
        offset < getContainerGridSpan(container);
        offset += 1
      ) {
        occupied.add(`${Number(x) + offset}-${Number(y)}`);
      }
    });

    (vessel?.dummies || []).forEach((position: any) => {
      if (Array.isArray(position) && position.length >= 2) {
        occupied.add(`${Number(position[0])}-${Number(position[1])}`);
      }
    });

    getExcludedPlannerPositionKeys(vessel).forEach((key) => occupied.add(key));

    return occupied.size;
  }, [allContainers, nextTerminalCall, planningContainers, terminalCall, vessel]);

  const freeFloorTeu = Math.max(0, floorCapacity - occupiedFloorTeu);

  const currentPositionContainers = useMemo(() => {
    const currentCallId = normalizeId(terminalCall?.["@id"]);
    const positioned = (planningContainers || []).filter(
      (container: any) =>
        Array.isArray(container?.position) &&
        container.position.length >= 3 &&
        normalizeId(container?.unloadingTerminalCall?.["@id"]) !== currentCallId
    );
    const dummies = (vessel?.dummies || []).map(
      (position: any, index: number) => ({
        "@id": `auto-planner-dummy-${index}`,
        uid: "",
        dummy: true,
        position: [1, Number(position?.[0]), Number(position?.[1])],
        containerType: { teu: 1 },
      })
    );

    const dummyKeys = new Set(
      (vessel?.dummies || []).map((position: any) =>
        plannerPositionKey(Number(position?.[0]), Number(position?.[1]))
      )
    );
    const exclusions = normalizeVesselPlannerSetting(
      vessel?.plannerSettings ?? vessel?.plannerSetting
    ).excludedPositions
      .filter(([x, y]) => !dummyKeys.has(plannerPositionKey(x, y)))
      .map(([x, y], index) => ({
        "@id": `auto-planner-exclusion-${index}`,
        uid: "",
        dummy: true,
        position: [1, x, y],
        containerType: { teu: 1 },
      }));

    return [...positioned, ...dummies, ...exclusions];
  }, [planningContainers, terminalCall, vessel]);

  const destinationGroups = useMemo(() => {
    const unknownLabel = t("unknown", "Unknown");
    const groups = new Map<string, any[]>();

    eligibleContainers.forEach((container: any) => {
      const name = destinationName(container, unknownLabel);
      groups.set(name, [...(groups.get(name) || []), container]);
    });

    return Array.from(groups.entries())
      .map(([name, groupContainers]) => {
        const totalTeu = groupContainers.reduce(
          (total, container) => total + getContainerTeu(container),
          0
        );
        return {
          name,
          containerCount: groupContainers.length,
          totalTeu,
          dividedTeu: totalTeu / maximumLayers,
          requiredGroundTeu: getRequiredGroundTeu(groupContainers, maximumLayers),
        };
      })
      .sort((left, right) => right.requiredGroundTeu - left.requiredGroundTeu);
  }, [eligibleContainers, maximumLayers, t]);

  const totalContainers = eligibleContainers.length;
  const totalTeu = eligibleContainers.reduce(
    (total, container) => total + getContainerTeu(container),
    0
  );
  const requiredGroundTeu = destinationGroups.reduce(
    (total, group) => total + group.requiredGroundTeu,
    0
  );
  const freeAfterPlanning = freeFloorTeu - requiredGroundTeu;
  const fits = freeAfterPlanning >= 0;
  const utilization = freeFloorTeu > 0 ? requiredGroundTeu / freeFloorTeu : 1;
  const isTightFit = fits && freeAfterPlanning <= 1;

  const minimumFittingLayers = useMemo(() => {
    for (let layers = 1; layers <= maximumVesselLayers; layers += 1) {
      const required = destinationGroups.reduce((total, group) => {
        const containers = eligibleContainers.filter(
          (container) => destinationName(container, t("unknown", "Unknown")) === group.name
        );
        return total + getRequiredGroundTeu(containers, layers);
      }, 0);
      if (required <= freeFloorTeu) return layers;
    }
    return null;
  }, [
    destinationGroups,
    eligibleContainers,
    freeFloorTeu,
    maximumVesselLayers,
    t,
  ]);

  const floorColumns = Array.isArray(vessel?.positions?.[0])
    ? vessel.positions[0].length
    : 0;
  const floorRows = Array.isArray(vessel?.positions?.[0])
    ? Math.max(0, ...vessel.positions[0].map((value: any) => Number(value) || 0))
    : 0;

  return (
    <>
    <Dialog
      open={open}
      onClose={processing ? undefined : onClose}
      maxWidth={false}
      fullWidth
      PaperProps={{
        sx: {
          width: "min(96vw, 1440px)",
          maxHeight: "94vh",
          m: 1.5,
          borderRadius: 3,
          overflow: "hidden",
        },
      }}
    >
      <DialogTitle sx={{ px: { xs: 2, md: 3 }, py: 2 }}>
        <Stack direction="row" alignItems="flex-start" spacing={1.5}>
          <Inventory2OutlinedIcon color="primary" sx={{ fontSize: 42 }} />
          <Box sx={{ flex: 1 }}>
            <Typography sx={{ fontSize: "1.35rem", fontWeight: 900, lineHeight: 1.2 }}>
              {t("autoPlannerConfigureTitle", "Autoplanner – Configure stowage")}
            </Typography>
            <Typography sx={{ mt: 0.35, color: "text.secondary", fontSize: "0.88rem" }}>
              {t(
                "autoPlannerConfigureSubtitle",
                "Choose the maximum stack height and review the floor space required per destination."
              )}
            </Typography>
          </Box>
          <Button
            onClick={() => setSettingsOpen(true)}
            disabled={processing}
            variant="outlined"
            size="small"
            startIcon={<SettingsOutlinedIcon />}
            sx={{
              alignSelf: "center",
              whiteSpace: "nowrap",
              textTransform: "none",
              display: { xs: "none", sm: "inline-flex" },
            }}
          >
            {t("autoPlannerVesselSettings", "Vessel settings")}
          </Button>
          <IconButton
            onClick={() => setSettingsOpen(true)}
            disabled={processing}
            aria-label={t("autoPlannerVesselSettings", "Vessel settings")}
            sx={{ display: { xs: "inline-flex", sm: "none" } }}
          >
            <SettingsOutlinedIcon />
          </IconButton>
          <IconButton onClick={onClose} disabled={processing} aria-label={t("close", "Close")}>
            <CloseIcon />
          </IconButton>
        </Stack>
      </DialogTitle>

      <Divider />

      <DialogContent sx={{ p: { xs: 1.5, md: 2.5 }, backgroundColor: "#f8fafc" }}>
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: { xs: "1fr", lg: "minmax(360px, 0.9fr) minmax(620px, 1.45fr)" },
            gap: 2,
          }}
        >
          <Paper variant="outlined" sx={{ p: 2, borderRadius: 2.5 }}>
            <Typography sx={{ fontWeight: 900, color: "#172554" }}>
              {t("autoPlannerChooseHeight", "1. Choose maximum stack height (layers)")}
            </Typography>
            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: `repeat(${Math.min(layerButtonCount, 5)}, minmax(64px, 1fr))`,
                gap: 1,
                mt: 2,
              }}
            >
              {Array.from(
                { length: layerButtonCount },
                (_, index) => index + 1
              ).map((layers) => (
                <Button
                  key={layers}
                  variant={maximumLayers === layers ? "outlined" : "text"}
                  disabled={temporarilyExcludedLayers.has(layers)}
                  onClick={() => setMaximumLayers(layers)}
                  sx={{
                    minHeight: 52,
                    border: "1px solid",
                    borderColor: maximumLayers === layers ? "primary.main" : "divider",
                    backgroundColor: maximumLayers === layers ? "#eff6ff" : "#fff",
                    color: maximumLayers === layers ? "primary.main" : "text.primary",
                    fontWeight: 850,
                    textTransform: "none",
                  }}
                >
                  {t("autoPlannerLayerCount", {
                    count: layers,
                    defaultValue: `${layers} layer${layers === 1 ? "" : "s"}`,
                  })}
                </Button>
              ))}
            </Box>

            <Alert severity="info" icon={<InfoOutlinedIcon />} sx={{ mt: 2 }}>
              <Typography sx={{ fontSize: "0.78rem", fontWeight: 800 }}>
                {t(
                  "autoPlannerHeightInfoTitle",
                  "The maximum stack height applies to every stack."
                )}
              </Typography>
              <Typography sx={{ fontSize: "0.75rem" }}>
                {t(
                  "autoPlannerHeightInfoText",
                  "The heaviest containers are placed on the lowest available layer."
                )}
              </Typography>
            </Alert>

            <Typography sx={{ mt: 2.25, fontWeight: 900, color: "#172554" }}>
              {t("autoPlannerStartPosition", "2. Starting position (current state)")}
            </Typography>
            <Box sx={{ py: 2.2, px: 1 }}>
              <AutoPlannerVesselGrid
                vessel={vessel}
                containers={currentPositionContainers}
                dockingSide={terminalCall?.dockingSide || "port"}
              />
              <Typography sx={{ mt: 1, textAlign: "center", fontSize: "0.75rem", fontWeight: 800 }}>
                {t("autoPlannerGridSummary", {
                  rows: floorRows,
                  columns: floorColumns,
                  total: floorCapacity,
                  defaultValue: `${floorRows} rows × ${floorColumns} positions = ${floorCapacity} ground positions`,
                })}
              </Typography>
            </Box>

            <Alert severity={occupiedFloorTeu === 0 ? "success" : "info"} sx={{ mb: 2 }}>
              <Typography sx={{ fontSize: "0.8rem", fontWeight: 850 }}>
                {occupiedFloorTeu === 0
                  ? t("autoPlannerVesselEmpty", "Vessel is empty")
                  : t("autoPlannerFloorOccupied", {
                      count: occupiedFloorTeu,
                      defaultValue: `${occupiedFloorTeu} TEU on the floor is already occupied`,
                    })}
              </Typography>
            </Alert>

            <Divider />
            <Box sx={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", mt: 2 }}>
              {[
                [<GridViewOutlinedIcon />, t("autoPlannerAvailableFloor", "Free floor TEU"), freeFloorTeu],
                [<LayersOutlinedIcon />, t("autoPlannerMaximumLayers", "Maximum layers"), maximumLayers],
                [<Inventory2OutlinedIcon />, t("autoPlannerTotalTeu", "Total TEU to load"), totalTeu],
                [<GridViewOutlinedIcon />, t("autoPlannerOccupancy", "Floor occupancy"), `${Math.round(utilization * 100)}%`],
              ].map(([icon, label, value], index) => (
                <Box key={String(label)} sx={{ px: 1, borderLeft: index ? "1px solid #e2e8f0" : "none" }}>
                  <Box sx={{ color: "primary.main", "& svg": { fontSize: 22 } }}>{icon}</Box>
                  <Typography sx={{ minHeight: 34, fontSize: "0.66rem", color: "text.secondary" }}>{label}</Typography>
                  <Typography sx={{ fontSize: "1rem", fontWeight: 900 }}>{value}</Typography>
                </Box>
              ))}
            </Box>
          </Paper>

          <Paper variant="outlined" sx={{ p: 1.5, borderRadius: 2.5, minWidth: 0 }}>
            <Typography sx={{ p: 0.5, fontWeight: 900, color: "#172554" }}>
              {t("autoPlannerDestinationNeeds", {
                layers: maximumLayers,
                defaultValue: `3. Destinations and floor-space requirement at ${maximumLayers} layers`,
              })}
            </Typography>
            <TableContainer sx={{ mt: 1 }}>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>{t("destination", "Destination")}</TableCell>
                    <TableCell align="right">{t("containers", "Containers")}</TableCell>
                    <TableCell align="right">{t("autoPlannerTotalTeu", "Total TEU")}</TableCell>
                    <TableCell align="right">
                      {t("autoPlannerTeuPerLayers", { layers: maximumLayers, defaultValue: `TEU / ${maximumLayers} layers` })}
                    </TableCell>
                    <TableCell align="right">{t("autoPlannerRequiredGround", "Required floor TEU")}</TableCell>
                    <TableCell align="right">{t("status", "Status")}</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {destinationGroups.map((group) => (
                    <TableRow key={group.name}>
                      <TableCell sx={{ fontWeight: 700 }}>{group.name}</TableCell>
                      <TableCell align="right">{group.containerCount}</TableCell>
                      <TableCell align="right">{group.totalTeu}</TableCell>
                      <TableCell align="right">{group.dividedTeu.toFixed(2)}</TableCell>
                      <TableCell align="right" sx={{ color: "secondary.dark", fontWeight: 900 }}>
                        {group.requiredGroundTeu} TEU
                      </TableCell>
                      <TableCell align="right">
                        <Stack direction="row" spacing={0.5} justifyContent="flex-end" alignItems="center">
                          {fits ? (
                            <CheckCircleOutlineIcon color="success" sx={{ fontSize: 18 }} />
                          ) : (
                            <WarningAmberRoundedIcon color="error" sx={{ fontSize: 18 }} />
                          )}
                          <Typography sx={{ fontSize: "0.75rem", fontWeight: 800, color: fits ? "success.dark" : "error.main" }}>
                            {fits ? t("ok", "OK") : t("shortage", "Shortage")}
                          </Typography>
                        </Stack>
                      </TableCell>
                    </TableRow>
                  ))}
                  <TableRow sx={{ backgroundColor: "#f5f3ff" }}>
                    <TableCell sx={{ fontWeight: 950 }}>{t("total", "Total")}</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 950 }}>{totalContainers}</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 950 }}>{totalTeu}</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 950 }}>{(totalTeu / maximumLayers).toFixed(2)}</TableCell>
                    <TableCell align="right" sx={{ color: "secondary.dark", fontWeight: 950 }}>{requiredGroundTeu} TEU</TableCell>
                    <TableCell align="right" sx={{ color: fits ? "success.dark" : "error.main", fontWeight: 950 }}>
                      {fits ? t("ok", "OK") : t("shortage", "Shortage")}
                    </TableCell>
                  </TableRow>
                </TableBody>
              </Table>
            </TableContainer>

            <Paper variant="outlined" sx={{ mt: 1.5, p: 1.25, borderRadius: 2, display: "flex", gap: 1.5, alignItems: "center" }}>
              <GridViewOutlinedIcon color="primary" sx={{ fontSize: 34 }} />
              <Typography sx={{ fontSize: "0.82rem" }}>
                <strong>{t("autoPlannerAvailableGround", "Available floor")}: {freeFloorTeu} TEU</strong>
                {" · "}{t("required", "Required")}: <strong>{requiredGroundTeu} TEU</strong>
                {" · "}<span style={{ color: fits ? "#15803d" : "#b91c1c", fontWeight: 800 }}>
                  {fits
                    ? t("autoPlannerFreeAfterPlanning", { count: freeAfterPlanning, defaultValue: `${freeAfterPlanning} TEU free` })
                    : t("autoPlannerTeuShort", { count: Math.abs(freeAfterPlanning), defaultValue: `${Math.abs(freeAfterPlanning)} TEU short` })}
                </span>
              </Typography>
            </Paper>

            <Alert severity="info" icon={<InfoOutlinedIcon />} sx={{ mt: 1.5 }}>
              <Typography sx={{ fontWeight: 900, fontSize: "0.82rem" }}>
                {t("autoPlannerCalculationTitle", "How is floor space calculated?")}
              </Typography>
              <Typography sx={{ mt: 0.5, fontSize: "0.76rem" }}>
                {t(
                  "autoPlannerCalculationText",
                  "For each destination, 40-foot containers get paired floor footprints (one 40-foot stack per layer each) and 20-foot containers get their own single floor cells (one 20-foot stack per layer each)."
                )}
              </Typography>
            </Alert>
          </Paper>
        </Box>

        {(isTightFit || !fits) && (
          <Alert severity={fits ? "warning" : "error"} icon={<WarningAmberRoundedIcon />} sx={{ mt: 2 }}>
            <Typography sx={{ fontWeight: 900 }}>
              {fits
                ? t("autoPlannerTightFit", "Caution: limited free floor space")
                : t("autoPlannerInsufficientHeight", "Insufficient stack height")}
            </Typography>
            <Typography sx={{ fontSize: "0.8rem" }}>
              {minimumFittingLayers && minimumFittingLayers > maximumLayers
                ? t("autoPlannerChooseMinimumLayers", {
                    count: minimumFittingLayers,
                    defaultValue: `Choose at least ${minimumFittingLayers} layers to fit all destination groups.`,
                  })
                : t(
                    "autoPlannerReviewWarning",
                    "Review the proposed stowage carefully; destination grouping and physical grid constraints can require additional room."
                  )}
            </Typography>
          </Alert>
        )}
      </DialogContent>

      <DialogActions sx={{ px: { xs: 2, md: 3 }, py: 1.75, gap: 1.25 }}>
        <Button onClick={onClose} disabled={processing} variant="outlined" sx={{ textTransform: "none" }}>
          {t("cancel", "Cancel")}
        </Button>
        <Button
          onClick={() => onConfirm(maximumLayers)}
          disabled={processing || totalContainers === 0 || !fits}
          variant="contained"
          endIcon={<ArrowForwardIcon />}
          sx={{ textTransform: "none", px: 2.5 }}
        >
          {t("autoPlannerContinue", "Continue to stowage plan")}
        </Button>
      </DialogActions>
    </Dialog>
    <VesselPlannerSettingsDialog
      open={settingsOpen}
      vessel={vessel}
      onClose={() => setSettingsOpen(false)}
      onSaved={(updatedVessel) => {
        onVesselUpdated?.(updatedVessel);
        setSettingsOpen(false);
      }}
    />
    </>
  );
};

export default AutoPlannerDialog;
